/* SignWarehouse /search results page on Typesense via InstantSearch.js + typesense-instantsearch-adapter.
 *
 * Flow: sw-search-core runs the pipeline once (SKU pass → full → relaxed, plus collections) with the
 * configured timeout. On success it hides the theme's native results (kept in the DOM, .sw-native-results)
 * and mounts InstantSearch into #sw-search-page with the same ranking parameters. On timeout or error the
 * native Shopify results stay visible and the query is logged with engine=native.
 *
 * Presentation deliberately reuses the theme's own markup and CSS so the page looks like the live one:
 * filter panels are <details class="filter disclosure">, options are `.checkbox + .filter__label` with
 * `(count)`, colours use the theme's `data-swatch` labels with Shopify's native swatch hex, and hits are
 * `.card.card--product` with the theme's inventory indicator. Facet order follows the live search page:
 * Category, Brand, Size, Color, Price, Availability, then Finish / Compatible with / Width / Length collapsed.
 *
 * Content rules: availability wording In stock / Low stock / Sold out, Drop Shipped for Route = Dropship (as the PDP); card labels as the
 * collection cards show them (Sold out, sale, merchtag badges); one price shown once with the
 * compare-at price struck beside it; no best-seller, trending or unit-count labels; no support or quote
 * calls to action. Clicks are logged by this script (position-aware).
 */
(function () {
  'use strict';
  if (typeof window === 'undefined' || !window.SWSearch || !window.SWSearch.configured()) return;
  var S = window.SWSearch, cfg = S.cfg, str = cfg.strings || {};
  if (!cfg.results) return;

  var container = document.getElementById('sw-search-page');
  if (!container) return;
  var q = (new URL(location.href)).searchParams.get('q') || container.getAttribute('data-q') || '';
  q = q.trim();
  if (!q) return;
  var nativeEl = document.querySelector('[data-sw-native]');

  function esc(s) { return S.escapeHtml(s); }
  function widthLabel(v) { return String(v) + ' in'; }
  var CHEVRON = '<svg width="24" height="24" viewBox="0 0 24 24" aria-hidden="true" focusable="false" role="presentation" class="icon"><path d="M20 8.5 12.5 16 5 8.5" stroke="currentColor" stroke-width="1.5" fill="none"/></svg>';

  // Facets in the live page's order. `open` panels start expanded; the rest are collapsed like the theme does
  // for secondary filters. Width is available but collapsed: the live theme hides it except on three collections.
  var FACETS = [
    { attr: 'vendor',        label: (str.facets || {}).brand || 'Brand',                  open: true,  limit: 7, showMore: 40, searchable: true },
    { attr: 'category',      label: (str.facets || {}).category || 'Category',            open: true,  limit: 7, showMore: 40 },
    { attr: 'keywords',      label: (str.facets || {}).keyword || 'Keyword',              open: true,  limit: 7, showMore: 60 },
    { attr: 'size',          label: (str.facets || {}).size || 'Size',                    open: true,  limit: 7, showMore: 120, sort: sizeSort },
    { attr: 'color_family',  label: (str.facets || {}).color || 'Color',                  open: true,  limit: 19, showMore: 19, swatch: true, sort: ['name:asc'] },
    { attr: 'rating_bucket', label: (str.facets || {}).rating || 'Customer Rating',       open: true,  limit: 5,  sort: ['name:desc'], stars: true },
    { attr: 'availability',  label: (str.facets || {}).availability || 'Availability',    open: true,  limit: 4,  labels: str.availabilityValues || {} },
    { attr: 'finish',        label: (str.facets || {}).finish || 'Finish',                open: false, limit: 7, showMore: 20 },
    { attr: 'compatibility', label: (str.facets || {}).compatibility || 'Compatible with',open: false, limit: 7, showMore: 60, searchable: true },
    { attr: 'width_label',   label: (str.facets || {}).width_label || 'Width',            open: false, limit: 7, showMore: 40, sort: numericSort },
    { attr: 'length_yd',     label: (str.facets || {}).length_yd || 'Length',             open: false, limit: 7, showMore: 30, sort: numericSort, suffix: ' yd' }
  ];
  // Same panels and order as the live filters (Search & Discovery), Brand first per Ryan: Brand = vendor,
  // Category = custom.category, Keyword = custom.keywords, Size = variant option, Color = shopify.color-pattern
  // taxonomy family with Shopify's swatch colours, Customer Rating = Yotpo average rounded to stars.
  var STAR = '<svg xmlns="http://www.w3.org/2000/svg" width="18" height="16" viewBox="0 0 18 16" fill="none" aria-hidden="true"><path d="M8.29538 0.581657C8.47147 0.0397099 9.23818 0.0397103 9.41427 0.581658L10.9679 5.36323C11.0467 5.6056 11.2725 5.76969 11.5273 5.76969H16.555C17.1248 5.76969 17.3618 6.49888 16.9007 6.83382L12.8333 9.789C12.6271 9.93879 12.5409 10.2043 12.6196 10.4467L14.1732 15.2282C14.3493 15.7702 13.729 16.2208 13.268 15.8859L9.20058 12.9307C8.99442 12.7809 8.71524 12.7809 8.50907 12.9307L4.44162 15.8859C3.98066 16.2208 3.36036 15.7702 3.53645 15.2282L5.09003 10.4467C5.16878 10.2043 5.08251 9.93879 4.87635 9.789L0.808894 6.83382C0.347934 6.49888 0.584849 5.76969 1.15464 5.76969H6.18232C6.43716 5.76969 6.663 5.6056 6.74174 5.36323L8.29538 0.581657Z" fill="#F5B301"/></svg>';
  function numericSort(a, b) { return parseFloat(a.name) - parseFloat(b.name); }
  function sizeSort(a, b) { var na = parseFloat(a.name), nb = parseFloat(b.name); return (isNaN(na) || isNaN(nb)) ? a.name.localeCompare(b.name) : na - nb || a.name.localeCompare(b.name); }

  // ---------------------------------------------------------------- 1. pipeline (with fallback)
  // main-search.liquid hides the theme's native results (html.sw-search-pending, 5 s failsafe) until this
  // answers, so buyers never see Shopify's listing flash first. Budget 1.5 s, then one 3 s retry in
  // S.search; native results show only when the host is really unreachable.

  // data-sw-engine on the container records which engine rendered this page load (typesense | native, with
  // data-sw-fallback = the reason and data-sw-ms = time to the answer). qa/tests/search.spec.mjs reads it to
  // decide per page load whether the Typesense assertions apply; it is also handy when debugging a live page.
  var started = Date.now();
  function mark(engine, why) {
    container.setAttribute('data-sw-engine', engine);
    document.documentElement.classList.remove('sw-search-pending');
    container.setAttribute('data-sw-ms', String(Date.now() - started));
    if (why) container.setAttribute('data-sw-fallback', String(why).slice(0, 160));
  }

  S.search(q, { per_page: 1, collections_per_page: 4, collectionDescription: true, suggestions: true, suggestions_per_page: 12, timeoutMs: Math.max(cfg.timeoutMs || 300, 1500), retryMs: 3000 }).then(function (res) {
    if (res.redirect) { S.logQuery(q, res, 'page', 'typesense'); location.replace(res.redirect); return; }
    if (!window.instantsearch || !window.TypesenseInstantSearchAdapter) throw new Error('instantsearch missing');
    // phase 3 reranker: only when the theme setting is on; S.rerank resolves null when pro3 has it off or is slow
    return S.rerank(q, cfg.rerankTimeoutMs || 350).then(function (order) { mount(res, order); });
  }).catch(function (err) {
    // 'error' = the host answered but mounting failed after the native results were hidden (a theme bug)
    mark(container.hidden ? 'native' : 'error', err ? (err.name && err.name !== 'Error' ? err.name + ': ' : '') + (err.message || err) : 'unknown');
    S.logQuery(q, null, 'page', 'native');
    if (window.console && console.debug) console.debug('[sw-search] native fallback:', err && err.message);
  });

  // ---------------------------------------------------------------- 2. InstantSearch

  var currentQid = null;

  /** order: reranked product ids for page one of the default sort (phase 3), or null */
  function mount(res, order) {
    var a = res.analysis;
    var hostUrl = new URL(cfg.host);
    var adapter = new window.TypesenseInstantSearchAdapter({
      server: {
        apiKey: cfg.key,
        nodes: [{ host: hostUrl.hostname, port: hostUrl.port || (hostUrl.protocol === 'https:' ? 443 : 80), protocol: hostUrl.protocol.replace(':', ''), path: hostUrl.pathname.replace(/\/$/, '') }],
        connectionTimeoutSeconds: 4,
        cacheSearchResultsForSeconds: 60
      },
      additionalSearchParameters: baseParams(a, res),
      collectionSpecificSearchParameters: {}
    });

    var indexName = cfg.collections.products;
    var search = window.instantsearch({
      indexName: indexName,
      searchClient: adapter.searchClient,
      routing: false,
      future: { preserveSharedStateOnUnmount: true },
      initialUiState: initialState(a, indexName)
    });

    container.innerHTML = layout(res);
    container.hidden = false;
    if (nativeEl) nativeEl.hidden = true;
    mark('typesense');

    var w = window.instantsearch.widgets;
    var widgets = [
      w.configure({ hitsPerPage: cfg.perPage || 24, query: a.residual || a.normalized }),
      w.stats({ container: '#sw-stats', templates: { text: function (d) {
        var total = d.nbHits || 0, per = d.hitsPerPage || (cfg.perPage || 24), from = total ? d.page * per + 1 : 0, to = Math.min(total, (d.page + 1) * per);
        var range = total > per ? from.toLocaleString() + '–' + to.toLocaleString() + ' ' + esc(str.of || 'of') + ' ' : '';
        return '<h1 class="sw-results-line">' + range + total.toLocaleString() + ' ' + esc(str.results || 'results') + ' ' + esc(str.for || 'for') + ' <strong>“' + esc(q) + '”</strong></h1>';
      } } }),
      w.sortBy({ container: '#sw-sort', cssClasses: { select: 'sw-toolbar__select' }, items: [
        { label: str.sortRelevance || 'Relevance', value: indexName },
        { label: str.sortPriceAsc || 'Price, low to high', value: indexName + '/sort/price_min:asc' },
        { label: str.sortPriceDesc || 'Price, high to low', value: indexName + '/sort/price_min:desc' },
        { label: str.sortNewest || 'Newest', value: indexName + '/sort/published_at:desc' }
      ] }),
      w.currentRefinements({ container: '#sw-current', transformItems: function (items) {
        return items.map(function (it) {
          var f = FACETS.filter(function (x) { return x.attr === it.attribute; })[0];
          it.label = f ? f.label : (it.attribute === 'price_min' ? (str.priceRange || 'Price') : it.label);
          it.refinements.forEach(function (r) { r.label = facetValueLabel(it.attribute, String(r.value), f); });
          return it;
        });
      } }),
      w.clearRefinements({ container: '#sw-clear', cssClasses: { button: 'link text-sm' }, templates: { resetLabel: function () { return esc(str.clearAll || 'Clear all'); } } }),
      w.hits({ container: '#sw-hits', templates: { item: hitTemplate, empty: function () { return ''; } },
        transformItems: function (items, meta) {
          var page = (meta && meta.results && meta.results.page) || 0, per = (meta && meta.results && meta.results.hitsPerPage) || (cfg.perPage || 24);
          if (order && page === 0 && untouched()) items = S.reorderByIds(items, order);
          return items.map(function (it, i) { it.__position = page * per + i + 1; return it; });
        },
        cssClasses: { list: GRID_OPEN, item: 'sw-hit' } }),
      w.pagination({ container: '#sw-pagination', padding: 2, showFirst: false, showLast: false })
    ];
    FACETS.forEach(function (f) {
      var el = document.getElementById('sw-facet-' + f.attr);
      if (el) widgets.push(themeRefinementList(Object.assign({ container: el, attribute: f.attr, limit: f.limit, showMoreLimit: f.showMore || f.limit, sortBy: f.sort ? f.sort : ['isRefined', 'count:desc', 'name:asc'] }, { facet: f })));
    });
    widgets.push(w.rangeInput({ container: '#sw-facet-price', attribute: 'price_min', precision: 0, cssClasses: { form: 'sw-price-range', input: 'input', submit: 'btn btn--primary btn--sm' }, templates: { separatorText: function () { return esc(str.to || 'to'); }, submitText: function () { return esc(str.go || 'Go'); } } }));
    search.addWidgets(widgets);

    /** The reranked order applies only to the default sort with no refinements. */
    function untouched() {
      var st = search.helper && search.helper.state;
      if (!st) return true;
      var dis = st.disjunctiveFacetsRefinements || {}, num = st.numericRefinements || {};
      return st.index === indexName && !Object.keys(dis).some(function (k) { return (dis[k] || []).length; }) && !Object.keys(num).length;
    }

    var lastLogged = null;
    search.on('render', function () {
      var r = search.helper && search.helper.lastResults;
      if (!r) return;
      var key = r.query + '|' + r.page + '|' + JSON.stringify(search.helper.state.disjunctiveFacetsRefinements || {}) + '|' + JSON.stringify(search.helper.state.numericRefinements || {});
      if (key !== lastLogged) {
        lastLogged = key;
        var fake = { found: r.nbHits, tookMs: r.processingTimeMS, analysis: a, products: { hits: r.hits.map(function (h) { return { document: { id: h.objectID || h.id } }; }) } };
        currentQid = S.logQuery(q, fake, 'page', 'typesense');
        var empty = document.getElementById('sw-empty');
        if (empty) { empty.hidden = r.nbHits > 0; if (r.nbHits === 0) empty.innerHTML = emptyState(res); }
      }
      initCardApps();
      injectPromo(res.promo, search.helper.state.page || 0);
      // hide panels whose facet has no values for this result set, like the theme does
      FACETS.forEach(function (f) {
        var el = document.getElementById('sw-facet-' + f.attr);
        var details = el && el.closest('details');
        if (details) details.hidden = !el.querySelector('li');
      });
    });
    search.start();

    container.addEventListener('click', function (evt) {
      var link = evt.target.closest('.sw-hit__link');
      if (!link) return;
      S.logClick(currentQid, link.getAttribute('data-product-id'), parseInt(link.getAttribute('data-position') || '0', 10) || null, null);
    });
    bindToolbar();
    renderNarrow(res);
  }

  /** Ranking parameters from the core builder, minus what InstantSearch manages itself. */
  function baseParams(a, res) {
    var p = S.buildProductSearch(a, { sku_pass: res.pass === 'sku', relaxed: res.pass === 'relaxed', purchased: res.purchased, facets: false, per_page: cfg.perPage || 24 });
    delete p.q; delete p.per_page; delete p.page; delete p.filter_by; delete p.facet_by; delete p.max_facet_values;
    p.highlight_full_fields = 'title,skus';
    return p;
  }

  /** Parsed dimensions become removable facet refinements instead of hard filters. */
  function initialState(a, indexName) {
    var st = {}; st[indexName] = { refinementList: {} };
    if (a.width_in !== null && a.width_in !== undefined) st[indexName].refinementList.width_label = [widthLabel(a.width_in)];
    if (a.length_yd !== null && a.length_yd !== undefined) st[indexName].refinementList.length_yd = [String(a.length_yd)];
    return st;
  }

  // ---------------------------------------------------------------- facet values

  /** "White|#ffffff" → {name: "White", hex: "#ffffff"}; other facets pass through. */
  function swatchParts(value) {
    var i = value.indexOf('|');
    return i === -1 ? { name: value, hex: '' } : { name: value.slice(0, i), hex: value.slice(i + 1) };
  }
  function facetValueLabel(attr, value, f) {
    if (attr === 'color_swatch' || attr === 'color_family') return swatchParts(value).name;
    if (attr === 'rating_bucket') return value + ' ' + (str.stars || 'stars');
    if (f && f.labels && f.labels[value]) return f.labels[value];
    if (f && f.suffix) return value + f.suffix;
    return value;
  }

  /** Customer Rating value "4" → four theme star icons (the live filter shows stars only; the text stays for screen readers). */
  function starsHtml(value, label) {
    var n = Math.max(1, Math.min(5, parseInt(value, 10) || 0)), out = '<span class="sw-stars" aria-hidden="true">';
    for (var i = 0; i < n; i++) out += STAR;
    return out + '</span><span class="visually-hidden">' + esc(label) + '</span>';
  }

  /**
   * Refinement list rendered with the theme's own filter markup (input.checkbox + label.filter__label, counts in
   * parentheses, data-swatch labels for colours) so it is indistinguishable from the native filters.
   */
  var themeRefinementList = window.instantsearch.connectors.connectRefinementList(function (params, isFirstRender) {
    var f = params.widgetParams.facet, el = params.widgetParams.container;
    if (isFirstRender) {
      el.addEventListener('change', function (evt) {
        var input = evt.target.closest('input[data-value]');
        if (input) params.refine(input.getAttribute('data-value'));
      });
      el.addEventListener('click', function (evt) {
        var more = evt.target.closest('.sw-show-more');
        if (more) { evt.preventDefault(); params.toggleShowMore(); }
      });
    }
    var items = params.items || [];
    if (!items.length) { el.innerHTML = ''; return; }
    var html = '<ul role="list"' + (f.swatch ? ' class="filter__swatches"' : '') + '>';
    items.forEach(function (it, i) {
      var id = 'sw-f-' + f.attr + '-' + i;
      var value = String(it.value);
      var sp = f.swatch ? swatchParts(value) : null;
      var label = facetValueLabel(f.attr, value, f);
      var swatchAttrs = '';
      if (f.swatch) {
        var hex = sp.hex && /^#[0-9a-f]{3,8}$/i.test(sp.hex) ? sp.hex : '';
        var special = /transparent|clear/i.test(sp.name) ? ' sw-swatch--transparent' : '';
        var multi = /multi/i.test(sp.name) && !hex;
        swatchAttrs = ' data-swatch title="' + esc(sp.name) + '"' + (hex ? ' style="--swatch-color: ' + esc(hex) + '; --swatch-image: none;"' : '') + (special ? ' data-swatch-special="1"' : '') + (multi ? ' data-swatch-multi="1"' : '');
      }
      html += '<li><input type="checkbox" class="' + (f.swatch ? '' : 'checkbox ') + 'visually-hidden focus-label-before" id="' + id + '" data-value="' + esc(value) + '"' + (it.isRefined ? ' checked' : '') + '>' +
        '<label for="' + id + '" class="filter__label flex items-start w-full m-0' + (f.swatch ? ' sw-swatch' : '') + '"' + swatchAttrs + '>' +
        '<span class="flex-auto">' + (f.stars ? starsHtml(value, label) : esc(label)) + '</span><span class="filter__count">(' + it.count + ')</span></label></li>';
    });
    html += '</ul>';
    if (params.canToggleShowMore) html += '<div class="disclosure__show-more"><button type="button" class="sw-show-more filter__more link text-sm">' + esc(params.isShowingMore ? (str.showLess || 'Show less') : (str.showMore || 'Show more')) + '</button></div>';
    el.innerHTML = html;
  });

  // ---------------------------------------------------------------- templates

  var ICON_FILTER = '<svg width="20" height="20" viewBox="0 0 20 20" stroke-width="1.25" stroke="currentColor" aria-hidden="true" focusable="false" role="presentation" class="icon"><path stroke-linecap="round" stroke-linejoin="round" d="M1 6h18M1 14h18"/><circle cx="7" cy="6" r="3"/><circle cx="13" cy="14" r="3"/></svg>';
  var GRID_OPEN = 'grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-x-theme gap-y-5 md:gap-y-8 sw-hits';
  var GRID_CLOSED = 'grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-x-theme gap-y-5 md:gap-y-8 sw-hits';
  function desktop() { return window.matchMedia && window.matchMedia('(min-width: 1024px)').matches; }
  function compareOn() {
    try { if (window.theme && theme.storageUtil) { var v = theme.storageUtil.get('compare-toggle'); if (v !== null && v !== undefined) return v === true || v === 'true'; } } catch (e) { /* default */ }
    return true; // the live theme ships with the compare toggle on
  }

  /**
   * Page top follows the live theme's products toolbar (Filter · Sort by · Compare) with an Amazon-style results
   * line above it ("1–24 of 108 results for “…”"), then "Narrow your search" chips, a collection content block
   * when the query names a collection, the filter rail and the grid.
   */
  function layout(res) {
    var html = '';
    if (res.pass === 'relaxed' && res.found > 0) html += '<div class="sw-search__notice">' + esc(str.broadened || 'No exact matches. Showing the closest results for') + ' “' + esc(q) + '”</div>';
    html += '<div id="sw-stats" class="sw-search__stats"></div>';
    html += '<div class="products-toolbar products-toolbar--compare flex items-center mb-8 sw-toolbar" id="sw-toolbar">' +
      '<div class="products-toolbar__filter"><button type="button" class="products-toolbar__filter-btn flex justify-between items-center text-current font-bold' + (desktop() ? ' is-active' : '') + '" id="sw-filters-toggle" aria-controls="sw-filters" aria-expanded="' + (desktop() ? 'true' : 'false') + '"><span>' + esc(str.filter || 'Filter') + '</span>' + ICON_FILTER + '</button></div>' +
      '<div class="products-toolbar__sort sw-toolbar__sort"><span class="label label--inline sw-toolbar__sort-label">' + esc(str.sortBy || 'Sort by') + '</span><span id="sw-sort"></span></div>' +
      '<div class="products-toolbar__compare end sw-toolbar__compare"><input type="checkbox" class="switch" id="sw-compare-switch"' + (compareOn() ? ' checked' : '') + '><label for="sw-compare-switch">' + esc(str.compare || 'Compare') + '</label></div>' +
      '</div>';
    html += '<div class="sw-narrow" id="sw-narrow" hidden></div>';
    html += '<div id="sw-current"></div>';
    html += '<div class="sw-search__layout main-products-grid" id="sw-grid" data-layout="grid" data-compare="' + (compareOn() ? 'true' : 'false') + '"><aside class="sw-search__filters main-products-grid__filters" id="sw-filters" data-collapsed="' + (desktop() ? 'false' : 'true') + '"><div class="sw-facets facets">';
    FACETS.forEach(function (f) {
      html += facetPanel(f.attr, f.label, f.open, '<div class="filter__content" id="sw-facet-' + f.attr + '"></div>');
      if (f.attr === 'color_family') html += facetPanel('price', str.priceRange || 'Price', true, '<div class="filter__content" id="sw-facet-price"></div>');
    });
    html += '<div id="sw-clear" class="mt-4"></div></div></aside>';
    html += '<div class="sw-search__main main-products-grid__results">' + collectionBlock(res) + '<div id="sw-empty" hidden></div><div id="sw-hits"></div><div id="sw-pagination"></div></div></div>';
    return html;
  }

  /** Content block: the collection the query names (title, image, first lines of its description). Automated: it
   *  reads the collection's own content, so the SEO hub copy marketing maintains shows up here without any rule. */
  function collectionBlock(res) {
    var match = strongCollections(res)[0];
    if (!match) return '';
    var d = match.document, text = String(d.description || '').replace(/\s+/g, ' ').trim();
    if (text.length > 180) text = text.slice(0, 177).replace(/\s+\S*$/, '') + '…';
    return '<a class="sw-block sw-block--collection" href="' + esc(d.url) + '">' +
      (d.image ? '<span class="sw-block__media"><img src="' + esc(S.imageUrl(d.image, 240)) + '" alt="" loading="lazy" width="120" height="120"></span>' : '') +
      '<span class="sw-block__body"><span class="sw-block__kicker text-xs">' + esc(str.shopCategory || 'Shop the category') + '</span><span class="sw-block__title font-bold">' + esc(d.title) + '</span>' +
      (text ? '<span class="sw-block__text text-sm">' + esc(text) + '</span>' : '') +
      '<span class="sw-block__cta text-sm">' + esc(str.seeAll || 'See everything in') + ' ' + esc(d.title) + ' →</span></span></a>';
  }

  /** "Narrow your search": chips built from what other buyers typed after this query (imported and live
   *  completions) plus collection titles that contain it. Each chip is a new search. */
  function renderNarrow(res) {
    var el = document.getElementById('sw-narrow');
    if (!el) return;
    var key = function (t) { return String(t).toLowerCase().replace(/[^a-z0-9 ]+/g, ' ').split(/\s+/).filter(Boolean).sort().join(' '); };
    var seen = {}; seen[key(q)] = 1;
    var chips = S.completions(res, 14).filter(function (c) { var k = key(c.text); if (!k || seen[k]) return false; seen[k] = 1; return true; });
    if (chips.length < 2) { el.hidden = true; return; }
    el.innerHTML = '<span class="sw-narrow__label">' + esc(str.narrow || 'Narrow your search') + '</span>' + chips.slice(0, 8).map(function (c) { return '<a class="sw-narrow__chip" href="' + esc(c.url) + '">' + esc(c.text) + '</a>'; }).join('');
    el.hidden = false;
  }

  /** Filter and Compare toggles behave like the theme's products toolbar; the compare drawer itself is the theme's. */
  function bindToolbar() {
    var toggle = document.getElementById('sw-filters-toggle'), rail = document.getElementById('sw-filters');
    if (toggle && rail) toggle.addEventListener('click', function () {
      var open = rail.getAttribute('data-collapsed') === 'true';
      rail.setAttribute('data-collapsed', open ? 'false' : 'true');
      toggle.classList.toggle('is-active', open);
      toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      var list = container.querySelector('.sw-hits');
      if (list) list.className = open || !desktop() ? GRID_OPEN : GRID_CLOSED;
    });
    var sw = document.getElementById('sw-compare-switch'), grid = document.getElementById('sw-grid');
    if (sw && grid) sw.addEventListener('change', function () {
      var on = sw.checked;
      grid.setAttribute('data-compare', on ? 'true' : 'false');
      var open = document.getElementById('compare-drawer-open');
      if (open) open.classList.toggle('is-out', !on);
      try { if (window.theme && theme.storageUtil) theme.storageUtil.set('compare-toggle', on); } catch (e) { /* optional */ }
      loadCompareResources();
    });
    if (compareOn()) loadCompareResources();
  }
  function loadCompareResources() {
    var tag = document.getElementById('compare-resources');
    if (!tag) return;
    try {
      JSON.parse(tag.textContent).forEach(function (r) {
        if (r.type === 'js') { var sc = document.createElement('script'); sc.src = r.path; document.head.appendChild(sc); }
        else if (r.type === 'css') { var l = document.createElement('link'); l.href = r.path; l.rel = 'stylesheet'; document.head.appendChild(l); }
      });
    } catch (e) { /* optional */ }
    tag.remove();
  }

  function facetPanel(key, label, open, inner) {
    return '<details class="filter disclosure sw-facet" id="sw-filter-' + esc(key) + '"' + (open ? ' open' : '') + '>' +
      '<summary class="filter__toggle"><div class="flex justify-between items-center"><span class="flex-auto font-bold">' + esc(label) + '</span><span class="disclosure__toggle">' + CHEVRON + '</span></div></summary>' +
      '<div class="disclosure__panel"><div class="disclosure__content">' + inner + '</div></div></details>';
  }

  function emptyState(res) {
    var hubs = ((res.collections && res.collections.hits) || []).slice(0, 4);
    var html = '<div class="sw-search__empty"><p>' + esc(str.noResults || 'No results for') + ' “' + esc(q) + '”.</p>';
    if (hubs.length) {
      html += '<p>' + esc(str.tryHubs || 'Browse these collections') + '</p><div class="sw-search__hubs">';
      hubs.forEach(function (h) { html += '<a href="' + esc(h.document.url) + '">' + esc(h.document.title) + '</a>'; });
      html += '</div>';
    }
    return html + '</div>';
  }

  /**
   * Theme card, same DOM as snippets/product-card.liquid so main.css / quick-add.css give it the live behaviour:
   * second-image swap on hover (.card__media.has-hover-image), "Choose options" quick-add button and wishlist
   * heart fading in over the image (.card__quick-add_wishlist; quick-add.js binds .js-quick-add through its
   * MutationObserver and the Wishlist Engine app is re-initialised after each render), Yotpo stars under the title.
   */
  function hitTemplate(hit) {
    var d = hit;
    var img = S.imageUrl(d.image, 400);
    var hover = d.image_hover ? S.imageUrl(d.image_hover, 400) : '';
    var title = safeHighlight(hit, 'title', d.title);
    var sku = matchedSkuHtml(hit);
    var dropShipped = d.availability === 'drop_shipped';
    var level = dropShipped ? 'drop_shipped' : (d.availability === 'low_stock' ? 'low' : (d.availability === 'sold_out' ? 'none' : 'normal'));
    // same classes as the PDP's inventory block (custom.css colours drop-shipped like the PDP)
    var stockClass = dropShipped ? 'product-inventory--drop-shipped' : (d.availability === 'sold_out' ? 'product-inventory--out-of-stock' : 'product-inventory--in-stock');
    var stockTitle = dropShipped && str.dropShippedNote ? ' title="' + esc(str.dropShippedNote) + '"' : '';
    var variants = []; try { variants = JSON.parse(d.variants_json || '[]'); } catch (e) { variants = []; }
    var firstVariant = variants.length ? variants[0].id : '';
    var singleVariant = variants.length === 1;
    var quickLabel = singleVariant ? (str.addToCart || 'Add to cart') : (str.chooseOptions || 'Choose options');
    var pos = hit.__position || '';
    var link = 'href="' + esc(d.url) + '" data-product-id="' + esc(d.id) + '" data-position="' + pos + '"';
    var PLUS = '<svg xmlns="http://www.w3.org/2000/svg" width="12" height="13" viewBox="0 0 12 13" fill="none" class="icon"><path d="M6 0.232422V12.2324" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/><path d="M0 6.23242H12" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/></svg>';
    var quickBtn = function (extraClass) {
      return '<button type="button" class="btn btn--primary w-full js-quick-add ' + extraClass + '" aria-haspopup="dialog" data-product-url="' + esc(d.url) + '" data-product-default-variant="' + (singleVariant ? 'true' : 'false') + '">' +
        '<span class="quick-add-btn-icon block pointer-events-none"><span class="visually-hidden">' + esc(quickLabel) + '</span>' + PLUS + '</span>' +
        '<span class="quick-add-btn-text block pointer-events-none">' + esc(quickLabel) + '</span></button>';
    };
    return '<product-card class="card card--product h-full card--product-contained card--no-lines card--product-compare relative flex sw-card">' +
      '<div class="card__compare no-js-hidden text-sm"><input type="checkbox" class="checkbox checkbox--compare js-compare-checkbox" id="sw-compare-' + esc(d.id) + '" data-product-id="' + esc(d.id) + '" data-product-url="' + esc(d.url) + '"><label for="sw-compare-' + esc(d.id) + '">' + esc(str.compare || 'Compare') + '</label></div>' +
      '<div class="card__media' + (hover ? ' has-hover-image' : '') + ' relative">' +
        '<a ' + link + ' aria-label="' + esc(d.title) + '" class="media block relative js-prod-link sw-hit__link" style="padding-top: 100%;" tabindex="-1">' +
          (img ? '<img src="' + esc(img) + '" class="img-fit card__main-image" loading="lazy" width="400" height="400" alt="">' : '') +
          (hover ? '<img src="' + esc(hover) + '" class="img-fit card__hover-image" loading="lazy" width="400" height="400" alt="">' : '') +
        '</a>' + labelsHtml(d, variants) + '</div>' +
      '<div class="card__info-container flex flex-col flex-auto relative"><div class="card__info w-full"><div class="card__info-inner flex flex-col h-full w-full">' +
        '<div class="card__title__wrapper"><p class="card__title font-bold mt-1 mb-0"><a ' + link + ' class="card-link text-current js-prod-link sw-hit__link">' + title + '</a></p>' + quickBtn('add-quick-trigger') + '</div>' +
        (sku ? '<div class="sw-hit__sku text-xs">' + sku + '</div>' : '') +
        ratingHtml(d) +
        '<div class="' + stockClass + '"><div class="pt-5 text-xs product-inventory__status" data-inventory-level="' + level + '"' + stockTitle + '>' + esc(S.availabilityLabel(d)) + '</div></div>' +
        '<div class="flex grow items-end"><div class="price price--bottom"><div class="price__default">' + S.priceHtml(d) + '</div></div></div>' +
      '</div></div>' +
      '<div class="card__quick-add_wishlist"><div class="card__quick-add mob:card__quick-add--icon"><div class="no-js-hidden">' + quickBtn('quick-add-main-btn') +
        '<a href="' + esc(d.url) + '" class="btn btn--secondary quick-add-view-btn">' + esc(str.viewDetails || 'View details') + '</a></div></div>' +
        '<div class="slider-ctrl wishlist-engine-btn btn"><div class="wishlist-engine wishlist-engine-invisible" data-product_id="' + esc(d.id) + '" data-variant_id="' + esc(firstVariant) + '" data-full_button="true" data-css="true"></div></div>' +
      '</div></div></product-card>';
  }

  /**
   * Merchandising slot: a curation rule's promo tile (title, text, image, link, button, position) rendered with
   * the theme's card--promo markup inside the results grid, first page only, re-inserted after every render.
   */
  function injectPromo(promo, page) {
    var list = container.querySelector('.sw-hits');
    if (!list) return;
    var old = list.querySelector('.sw-hit--promo');
    if (old) old.remove();
    if (!promo || page > 0) return;
    var items = list.children;
    if (!items.length) return;
    var li = document.createElement('li');
    li.className = 'sw-hit sw-hit--promo';
    var link = promo.link_url ? esc(promo.link_url) : '';
    var html = '<div class="card card--promo relative flex flex-col h-full card-promo--noscheme sw-promo">';
    if (promo.image_url) {
      html += '<div class="card__media card--promo__media relative"><div class="media relative" style="padding-top: 100%;">' +
        (link ? '<a href="' + link + '" tabindex="-1" aria-hidden="true">' : '') + '<img src="' + esc(promo.image_url) + '" alt="" loading="lazy" class="img-fit">' + (link ? '</a>' : '') + '</div></div>';
    }
    html += '<div class="card--promo__info card__info flex flex-col promo--grow"><div class="mb-4 last:mb-0 promo--grow">';
    if (promo.title) html += '<h2 class="card__title h6">' + (link && !promo.button_label ? '<a href="' + link + '" class="card-link text-current">' + esc(promo.title) + '</a>' : esc(promo.title)) + '</h2>';
    if (promo.text) html += '<div class="card--promo__content relative rte"><p>' + esc(promo.text) + '</p></div>';
    html += '</div>';
    if (promo.button_label) html += '<a class="btn btn--primary w-full card--promo__infobtn"' + (link ? ' href="' + link + '"' : ' role="link" aria-disabled="true"') + '>' + esc(promo.button_label) + '</a>';
    html += '</div></div>';
    li.innerHTML = html;
    var idx = Math.max(0, Math.min(items.length, (parseInt(promo.position, 10) || 3) - 1));
    list.insertBefore(li, items[idx] || null);
  }

  /**
   * Card labels, as snippets/product-label.liquid renders them on collection cards (same classes, settings and
   * wording, two at most): Sold out, then the sale label from the variants' compare-at prices (largest discount;
   * "Up to" when the product has more than one variant), then the merchandising badges indexed from the
   * merchtag:<Title>:<colour> tags (field `badges`, "Title|colour").
   */
  function labelsHtml(d, variants) {
    var L = cfg.labels || {};
    if (!L.position || L.position === 'none') return '';
    var limit = L.limit || 2, out = [];
    var end = L.position.indexOf('end') > -1;
    var wrap = function (cls, text) {
      return '<div class="product-label-wrapper flex' + (end ? ' justify-end' : '') + '"><span class="product-label ' + cls + '">' + text + '</span></div>';
    };
    if (L.soldOut && d.availability === 'sold_out') out.push(wrap('product-label--sold-out', esc(str.soldOut || 'Sold out')));
    if (L.sale && out.length < limit) {
      var best = 0;
      variants.forEach(function (v) {
        var p = parseFloat(v.price), c = parseFloat(v.compare_at);
        if (!(p > 0) || !(c > p)) return;
        var x = L.saleType === 'percent' ? Math.round((1 - p / c) * 100) : c - p;
        if (x > best) best = x;
      });
      if (best > 0) {
        var one = variants.length === 1, text;
        if (L.saleType === 'percent') text = (one ? str.labelPercent : str.labelPercentUpTo) || '%A%% off';
        else if (L.saleType === 'money') text = (one ? str.labelMoney : str.labelMoneyUpTo) || '%A% off';
        else text = str.labelSale || 'Sale';
        text = text.replace('%A%', L.saleType === 'money' ? S.money(best) : String(best));
        out.push(wrap('product-label--sale', '-' + esc(text)));
      }
    }
    (d.badges || []).forEach(function (b) {
      if (out.length >= limit) return;
      var parts = String(b).split('|'), title = parts[0], colour = (parts[1] || '').toLowerCase().replace(/\s+/g, '').replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
      if (!title || !colour) return;
      out.push(wrap('product-label--custom product-label--custom-' + colour, esc(title)));
    });
    return out.length ? '<div class="product-label-container absolute ' + esc(L.position) + '">' + out.join('') + '</div>' : '';
  }

  /** Star rating from the indexed Yotpo average and count (rendered by us, so it shows without waiting for the widget). */
  var STAR_BASE = STAR.replace('fill="#F5B301"', 'fill="#d9d9d9"');
  function ratingHtml(d) {
    var n = parseInt(d.rating_count, 10) || 0, r = parseFloat(d.rating);
    if (!n || !r) return '';
    var pct = Math.max(0, Math.min(100, Math.round(r / 5 * 100)));
    var five = function (svg) { var o = ''; for (var i = 0; i < 5; i++) o += svg; return o; };
    return '<div class="sw-rating" aria-label="' + esc(r.toFixed(1)) + ' ' + esc(str.outOf5 || 'out of 5') + ', ' + n.toLocaleString() + ' ' + esc(str.reviews || 'reviews') + '">' +
      '<span class="sw-rating__stars" aria-hidden="true"><span class="sw-rating__base">' + five(STAR_BASE) + '</span><span class="sw-rating__fill" style="width:' + pct + '%">' + five(STAR) + '</span></span>' +
      '<span class="sw-rating__count text-xs" aria-hidden="true">' + esc(r.toFixed(1)) + ' (' + n.toLocaleString() + ')</span></div>';
  }

  /** Third-party widgets scan the DOM once on load; re-run them for the cards InstantSearch just rendered. */
  function initCardApps() {
    try { if (typeof window.WishEngineinit === 'function') window.WishEngineinit(); } catch (e) { /* optional */ }
  }

  function safeHighlight(hit, attribute, fallback) {
    try {
      var h = hit._highlightResult && hit._highlightResult[attribute];
      if (h && h.value) return window.instantsearch.highlight({ attribute: attribute, hit: hit });
    } catch (e) { /* fall through */ }
    return esc(fallback);
  }

  function matchedSkuHtml(hit) {
    var arr = hit._highlightResult && hit._highlightResult.skus;
    if (!Array.isArray(arr)) return null;
    for (var i = 0; i < arr.length; i++) {
      if (arr[i] && arr[i].matchLevel && arr[i].matchLevel !== 'none') {
        try { return window.instantsearch.highlight({ attribute: 'skus.' + i, hit: hit }); } catch (e) { return esc(hit.skus[i]); }
      }
    }
    return null;
  }

  function strongCollections(res) {
    var a = res.analysis, qn = a.residual || a.normalized;
    return ((res.collections && res.collections.hits) || []).filter(function (h) {
      var title = String(h.document.title || '').toLowerCase().replace(/\s+/g, ' ').trim();
      if (!qn) return false;
      if (title === qn) return true;
      var qt = qn.split(' '), tt = title.replace(/[^a-z0-9 ]+/g, ' ').split(/\s+/).filter(Boolean);
      var all = qt.every(function (t) { return tt.indexOf(t) !== -1; });
      return all && (h.document.is_hub || tt.length <= qt.length + 3);
    });
  }
})();
